import React, { useEffect, useState, useRef } from 'react';
import { getLenis, scrollToTop, smoothScrollTo } from '../../utils/smoothScroll';
import { motion } from 'framer-motion';

const TestScroll = () => {
  const [scrollInfo, setScrollInfo] = useState({
    progress: 0,
    velocity: 0,
    direction: null,
    position: 0
  });
  
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);
  
  useEffect(() => {
    // Smooth Scroll Eventi Dinle
    const handleSmoothScroll = (e) => {
      const { position, velocity, direction, progress } = e.detail;
      setScrollInfo({
        progress,
        velocity,
        direction,
        position
      });
    };
    
    window.addEventListener('smoothScroll', handleSmoothScroll);
    
    return () => {
      window.removeEventListener('smoothScroll', handleSmoothScroll);
    };
  }, []);
  
  // Lenis instance'ını kontrol et ve farklı durumlara geçir
  const toggleSmoothScroll = () => {
    const lenis = getLenis();
    if (lenis) {
      if (lenis.isStopped) {
        lenis.start();
      } else {
        lenis.stop();
      }
    }
  };
  
  // Belirli bir bölüme kaydır
  const scrollToSection = (ref) => {
    if (ref.current) {
      smoothScrollTo(ref.current, {
        offset: -100,
        duration: 1.5,
        easing: (t) => 1 - Math.pow(1 - t, 5) // Power 5 ease out
      });
    }
  };
  
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Sabit üst bilgi paneli */}
      <div className="fixed top-0 left-0 w-full bg-white dark:bg-gray-800 shadow-md z-50 p-4">
        <div className="flex flex-col md:flex-row justify-between items-center max-w-7xl mx-auto px-4">
          <h2 className="text-xl font-bold mb-4 md:mb-0 text-gray-800 dark:text-white">
            Lenis Smooth Scroll Test
          </h2>
          
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="text-sm text-gray-600 dark:text-gray-300 grid grid-cols-2 gap-x-4 gap-y-1">
              <span>İlerleme:</span>
              <span className="font-mono">{scrollInfo.progress.toFixed(3)}</span>
              
              <span>Hız:</span>
              <span className="font-mono">{scrollInfo.velocity.toFixed(3)}</span>
              
              <span>Yön:</span>
              <span className="font-mono">{scrollInfo.direction}</span>
              
              <span>Pozisyon:</span>
              <span className="font-mono">{Math.round(scrollInfo.position)}px</span>
            </div>
            
            <div className="flex gap-2 mt-4 md:mt-0">
              <button 
                onClick={toggleSmoothScroll}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Toggle Smooth Scroll
              </button>
              
              <button 
                onClick={() => scrollToTop({ duration: 2 })}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
              >
                Başa Dön
              </button>
            </div>
          </div>
        </div>
        
        {/* Bölüm navigasyonu */}
        <div className="flex justify-center gap-4 mt-4">
          <button 
            onClick={() => scrollToSection(section1Ref)}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg"
          >
            Bölüm 1
          </button>
          <button 
            onClick={() => scrollToSection(section2Ref)}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg"
          >
            Bölüm 2
          </button>
          <button 
            onClick={() => scrollToSection(section3Ref)}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg"
          >
            Bölüm 3
          </button>
        </div>
      </div>
      
      {/* Scroll ilerleme göstergesi */}
      <div 
        className="fixed top-32 left-0 h-1 bg-blue-600 transition-all duration-200 z-50"
        style={{ width: `${scrollInfo.progress * 100}%` }}
      ></div>
      
      {/* İçerik Bölümleri */}
      <div className="pt-48 pb-20">
        {/* Bölüm 1 */}
        <section 
          ref={section1Ref}
          className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-800 scroll-section"
        >
          <div className="max-w-4xl mx-auto px-4 py-20">
            <h2 className="text-4xl font-bold mb-6 text-gray-900 dark:text-white text-center">
              Bölüm 1: Paralaks Efektler
            </h2>
            
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-10 mb-10">
              <div className="flex gap-10 flex-wrap">
                {[...Array(6)].map((_, index) => (
                  <motion.div
                    key={index}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-64 h-64 flex items-center justify-center"
                    style={{
                      translateY: scrollInfo.position * 0.1 * (index * 0.1 + 0.1),
                      rotate: scrollInfo.position * 0.01 * (index % 2 === 0 ? 1 : -1),
                      scale: 1 - Math.min(0.2, Math.max(0, scrollInfo.progress - 0.1) * 0.3)
                    }}
                  >
                    <span className="text-xl font-bold text-gray-800 dark:text-white">
                      Kutu {index + 1}
                    </span>
                  </motion.div>
                ))}
              </div>
              
              <div 
                className="absolute top-10 right-10 w-40 h-40 rounded-full bg-blue-500/20 blur-3xl"
                style={{
                  transform: `translate(${scrollInfo.progress * 100}px, ${-scrollInfo.progress * 200}px)`,
                }}
              ></div>
            </div>
            
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
              Lenis, ekran kaydırma deneyimini tamamen değiştiren çok hafif, güçlü ve inertia tabanlı bir kütüphanedir. 
              Kaydırma sırasında animasyonları simüle etmek için normal bir JavaScript kütüphanesinden çok daha akıcı ve doğal bir his verir.
            </p>
            
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
              Lenis'in en etkileyici yanlarından biri fizik tabanlı kaydırma mekanizmasıdır. 
              Bu, kullanıcının kaydırma hareketlerini takip eder ve onlara gerçekçi bir atalet hissi vermek için 
              ivmelenme ve yavaşlama efektleri uygular.
            </p>
          </div>
        </section>
        
        {/* Bölüm 2 */}
        <section 
          ref={section2Ref}
          className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 scroll-section"
        >
          <div className="max-w-4xl mx-auto px-4 py-20">
            <h2 className="text-4xl font-bold mb-10 text-gray-900 dark:text-white text-center">
              Bölüm 2: Akıcı Hareketler
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {[...Array(3)].map((_, index) => (
                <motion.div
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
                  style={{
                    translateY: (scrollInfo.position - 1000) * 0.1 * (index * 0.2 + 0.1),
                    opacity: Math.min(1, Math.max(0, 1 - Math.abs(scrollInfo.progress - 0.5) * 3))
                  }}
                >
                  <div className="h-40 bg-gradient-to-br from-purple-500 to-blue-500"></div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                      Özellik {index + 1}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      Smooth scrolling, kullanıcı deneyimini büyük ölçüde artırır ve sitenize
                      profesyonel bir his katar.
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p>
                Lenis ile düzgün kaydırma, animasyonlar ve atalet efektlerini kolayca uygulayabilirsiniz. 
                Bu kütüphane, fizik tabanlı animasyonlar ve doğal yavaşlama efektleriyle kullanıcılara 
                premium bir kaydırma deneyimi sunar.
              </p>
              
              <p>
                Lenis, GSAP ScrollTrigger gibi diğer animasyon kütüphaneleriyle birlikte çalışabilir, 
                böylece daha karmaşık animasyon senaryoları oluşturabilirsiniz.
              </p>
              
              <p>
                Ayrıca, çok yönlü bir API ile gelir ve hem yatay hem de dikey kaydırmayı destekler. 
                Özelleştirilebilir seçeneklerle, web sayfanızın kaydırma davranışını tam olarak kontrol 
                edebilirsiniz.
              </p>
            </div>
          </div>
        </section>
        
        {/* Bölüm 3 */}
        <section 
          ref={section3Ref}
          className="min-h-screen flex items-center justify-center bg-gray-800 dark:bg-black scroll-section"
        >
          <div className="max-w-4xl mx-auto px-4 py-20">
            <h2 className="text-4xl font-bold mb-10 text-white text-center">
              Bölüm 3: Özel Efektler
            </h2>
            
            <div className="relative h-96 rounded-xl overflow-hidden mb-10">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1579547945413-497e1b99dac0?auto=format&fit=crop&w=1200)',
                  transformOrigin: '50% 100%',
                  transform: `scale(${1 + scrollInfo.progress * 0.2}) translateY(${scrollInfo.progress * -50}px)`,
                }}
              ></div>
              
              <div 
                className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent opacity-70"
                style={{
                  opacity: 0.7 + scrollInfo.progress * 0.3
                }}  
              ></div>
              
              <div className="absolute inset-0 flex items-center justify-center">
                <h3 
                  className="text-5xl font-bold text-white text-center px-6 py-4"
                  style={{
                    transform: `translateY(${scrollInfo.progress * 50}px)`,
                    opacity: 1 - scrollInfo.progress * 1.5
                  }}
                >
                  Paralaks Efektler
                </h3>
              </div>
            </div>
            
            <p className="text-xl text-gray-300 mb-8">
              Lenis, web sitenizde etkileyici özel efektler oluşturmanıza yardımcı olabilir.
              Kaydırma pozisyonu, hızı ve yönü hakkında gerçek zamanlı bilgilere 
              erişebilir, bunları kullanarak görsel öğelerinizi canlandırabilirsiniz.
            </p>
            
            {/* Örnek Butonlar */}
            <div className="flex flex-wrap gap-5 justify-center">
              {['Pürüzsüz', 'Doğal', 'Profesyonel', 'Etkileyici', 'Üstün', 'Premium'].map((text, index) => (
                <motion.button
                  key={index}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-xl"
                  style={{
                    translateY: Math.sin(scrollInfo.position / 100 + index) * 20,
                    opacity: 0.7 + Math.sin(scrollInfo.position / 200 + index) * 0.3
                  }}
                >
                  {text} Kaydırma
                </motion.button>
              ))}
            </div>
          </div>
        </section>
        
        {/* Bitiş Bölümü */}
        <section className="py-32 bg-gray-100 dark:bg-gray-800 scroll-section text-center">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold mb-6 text-gray-900 dark:text-white">
              Ultra Pürüzsüz Kaydırma Deneyimi
            </h2>
            
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-10">
              Lenis kütüphanesi ile sitenizde profesyonel ve akıcı bir kaydırma deneyimi sunun.
            </p>
            
            <button
              onClick={() => scrollToTop()}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl text-lg shadow-lg transform transition-transform hover:scale-105"
            >
              Başa Dön
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TestScroll;

 